import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { useAppSelector } from "../../app/hooks";
import { isAdminRole, UserRole } from "../../app/userSlice";
import Avatar from "../../components/Avatar";
import { useEndImpersonationMutation } from "../impersonation/impersonationApiSlice";
import { useImpersonationHandoff } from "../impersonation/useImpersonationHandoff";

const basicUserNavigation = [
	{ id: "account", name: "My Account", href: "/myAccount" },
	{ id: "alerts", name: "Manage Alerts", href: "/myAlerts" },
	{ id: "logout", name: "Log out", href: "/logout" },
];

export default function ProfileMenu() {
	const {
		userId,
		hasImage,
		initials,
		firstName,
		lastName,
		role,
		isTeachersWriterAccount,
		requiresAccountConfirmation,
		isImpersonating,
	} = useAppSelector((state) => state.user);

	const [menuItems, setMenuItems] = useState(basicUserNavigation);

	const [endImpersonation] = useEndImpersonationMutation();
	const { handleImpersonationResult, showImpersonationError } = useImpersonationHandoff();

	const endImpersonationErrorMessage = "There was an error ending impersonation.";

	const handleEndImpersonation = () => {
		endImpersonation()
			.unwrap()
			.then((result) => {
				handleImpersonationResult(result, endImpersonationErrorMessage);
			})
			.catch(() => {
				showImpersonationError(undefined, endImpersonationErrorMessage);
			});
	};

	useEffect(() => {
		let newMenuItems: typeof basicUserNavigation = [...basicUserNavigation];

		// TODO: when impersonation for super admins gets added, allow super admins into this to create a name for them
		// TODO: when impersonating a teacher, also need the sign in as student option additionally to the regular impersonate option
		const canViewAsAnotherRole =
			role === UserRole.Proctor || (role === UserRole.Writer && isTeachersWriterAccount);

		if (canViewAsAnotherRole) {
			const secondToLastIndex = basicUserNavigation.length - 1;

			const name = role === UserRole.Proctor ? "View as Student" : "View as Teacher";

			newMenuItems = [
				...basicUserNavigation.slice(0, secondToLastIndex),
				{ id: "impersonate", name: name, href: "/impersonation" },
				...basicUserNavigation.slice(secondToLastIndex),
			];

			if (requiresAccountConfirmation && !isImpersonating) {
				newMenuItems = newMenuItems.map((item) =>
					item.id === "alerts" || item.id === "impersonate"
						? { ...item, href: "/myAccount" }
						: item
				);
			}
		}

		// While impersonating, offer a way to end impersonation without logging out
		// entirely. Skip the teacher/student "View as" case: that flow is also built
		// on impersonation, but "View as Teacher" (above) is its intended exit, so
		// showing "End impersonation" there would be redundant.
		const isTeacherStudentView = role === UserRole.Writer && isTeachersWriterAccount;
		if (isImpersonating && !isTeacherStudentView) {
			const logoutIndex = newMenuItems.findIndex((item) => item.id === "logout");
			const insertAt = logoutIndex >= 0 ? logoutIndex : newMenuItems.length;
			newMenuItems = [
				...newMenuItems.slice(0, insertAt),
				{ id: "endImpersonation", name: "End impersonation", href: "" },
				...newMenuItems.slice(insertAt),
			];
		}

		setMenuItems(newMenuItems);
	}, [requiresAccountConfirmation, isImpersonating, role, isTeachersWriterAccount]);

	return (
		<Menu as="div">
			<MenuButton id="profile-button" className="flex justify-center items-center">
				<div
					data-tooltip-id="avatar-icon-tooltip"
					className="h-10 w-10 flex cursor-pointer justify-center items-center rounded-full ring-2 ring-white/10 hover:ring-white/60">
					<span className="sr-only">Open user profile menu</span>

					<Avatar
						hasImage={hasImage ?? false}
						initials={initials ?? ""}
						userId={userId ?? 0}
						override={true}
						textClassName="text-white hover:opacity-85"
						borderClassName=""
						bgClassName="bg-white/10 hover:bg-white/30"
					/>
				</div>
				<Tooltip
					id="avatar-icon-tooltip"
					place="bottom-end"
					content={`${firstName} ${lastName}${isTeachersWriterAccount ? " (Student)" : ""}`}
					className="z-40"></Tooltip>
			</MenuButton>
			<Transition
				as={Fragment}
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95">
				<MenuItems className="absolute -right-2 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
					{menuItems.map((item) => {
						return (
							<MenuItem key={item.name}>
								{({ active }) =>
									item.id === "endImpersonation" ? (
										<button
											type="button"
											id="end-impersonation-button"
											onClick={handleEndImpersonation}
											className={`${
												active ? "bg-gray-100" : ""
											} block w-full px-4 py-2 text-left text-sm text-gray-700`}>
											{item.name}
										</button>
									) : (
										<Link
											to={item.href}
											className={`${
												active ? "bg-gray-100" : ""
											} block px-4 py-2 text-sm text-gray-700`}>
											{item.name}
										</Link>
									)
								}
							</MenuItem>
						);
					})}
				</MenuItems>
			</Transition>
		</Menu>
	);
}
